
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { VisibilityConfigDialog } from "./VisibilityConfigDialog";
import { useProfile } from "@/hooks/useProfile";

interface RoutinesListProps {
  routines: any[];
  isLoading: boolean;
  getInstructorName: (item: any) => string;
  onRoutineDeleted?: () => void;
}

export const RoutinesList = ({ routines, isLoading, getInstructorName, onRoutineDeleted }: RoutinesListProps) => {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUserId(user?.id ?? null);
    });
  }, []);

  const handleCopyRoutine = async (routine: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no autenticado");

      const { data: newRoutine, error: routineError } = await supabase
        .from("routines")
        .insert({
          name: `${routine.name} (Copiada)`,
          user_id: user.id
        })
        .select()
        .single();

      if (routineError) throw routineError;

      const { data: exercises, error: exercisesError } = await supabase
        .from("exercises")
        .select("*")
        .eq("routine_id", routine.id);

      if (exercisesError) throw exercisesError;

      if (exercises && exercises.length > 0) {
        const newExercises = exercises.map(({ id, created_at, updated_at, ...exercise }) => ({
          ...exercise,
          routine_id: newRoutine.id,
        }));

        const { error: insertError } = await supabase
          .from("exercises")
          .insert(newExercises);

        if (insertError) throw insertError;
      }

      toast({
        title: "Rutina copiada",
        description: "La rutina se ha copiado exitosamente a tu lista de rutinas",
      });

      navigate("/rutina");
    } catch (error: any) {
      console.error("Error copying routine:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "No se pudo copiar la rutina",
      });
    }
  };

  const handleDeleteRoutine = async (routineId: string) => {
    try {
      const { error: exercisesError } = await supabase
        .from("exercises")
        .delete()
        .eq("routine_id", routineId);

      if (exercisesError) throw exercisesError;

      const { error: daysError } = await supabase
        .from("routine_days")
        .delete()
        .eq("routine_id", routineId);

      if (daysError) throw daysError;

      // Unlink from any workout plans
      const { error: unlinkError } = await supabase
        .from("workout_plan_routines")
        .delete()
        .eq("routine_id", routineId);

      if (unlinkError) throw unlinkError;

      const { error: deleteError } = await supabase
        .from("routines")
        .delete()
        .eq("id", routineId);

      if (deleteError) throw deleteError;

      toast({
        title: "Rutina eliminada",
        description: "La rutina se ha eliminado exitosamente",
        variant: "success",
      });

      onRoutineDeleted?.();
    } catch (error: any) {
      console.error("Error deleting routine:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "No se pudo eliminar la rutina",
      });
    }
  };

  if (isLoading) {
    return <div className="text-center text-muted-foreground">Cargando rutinas...</div>;
  }

  if (routines?.length === 0) {
    return <div className="text-center text-muted-foreground">No se encontraron rutinas</div>;
  }

  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
      {routines?.map((routine) => (
        <div
          key={routine.id}
          className="bg-card backdrop-blur-lg rounded-xl p-4 border border-border shadow-sm transition-all hover:shadow-md hover:border-primary"
        >
          <h3 className="text-lg font-semibold mb-2 text-primary">{routine.name}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Creada por: {getInstructorName(routine)}
          </p>
          <div className="flex gap-2">
            <Button
              onClick={() => handleCopyRoutine(routine)}
              className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              Copiar Rutina
            </Button>
            {routine.user_id === currentUserId && profile?.gym_id && (
              <VisibilityConfigDialog
                contentType="routine"
                contentId={routine.id}
                gymId={profile.gym_id}
              />
            )}
            {routine.user_id === currentUserId && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="bg-destructive/10 border-destructive/30 hover:bg-destructive/20 text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta acción no se puede deshacer. La rutina "{routine.name}" será eliminada permanentemente.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleDeleteRoutine(routine.id)}
                      className="bg-destructive hover:bg-destructive/90"
                    >
                      Eliminar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
